import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { Box, LinearProgress, Toolbar, useMediaQuery, alpha } from '@mui/material';
import { DRAWER_WIDTH, APPBAR_HEIGHT } from './styles/layout';
import { MigasAutomaticas, MigasProvider } from './componentes/ui/Migas';
import AppBar from './componentes/navegacion/Appbar';
import Sidebar from './componentes/navegacion/Sidebar';
import Home from './vistas/Home';
import LoginForm from './vistas/Login';
import AuthRedirect from './componentes/security/AuthRedirect';
import ProtectedRoute from './componentes/security/ProtectedRoute';
// ── Suscriptor (login propio + bandeja) ──
//import SuscriptorLogin from './vistas/Suscriptor/SuscriptorLogin';
//import LandingPage from './vistas/LandingPage';
import { useUserStatusValidator } from './hooks/useUserStatusValidator';

// Cada vista se descarga al visitarla (carga inicial más liviana).
// Si un fragmento quedó obsoleto tras un despliegue, se recarga la página una sola vez.
const cargarVista = (importar) =>
  lazy(() =>
    importar()
      .then((modulo) => {
        sessionStorage.removeItem('vista-recargada');
        return modulo;
      })
      .catch((error) => {
        if (!sessionStorage.getItem('vista-recargada')) {
          sessionStorage.setItem('vista-recargada', '1');
          window.location.reload();
        }
        throw error;
      })
  );

const CargandoVista = () => (
  <Box sx={{ pt: 1 }} role="progressbar" aria-label="Cargando">
    <LinearProgress sx={{ height: 3 }} />
  </Box>
);

const NuevaNormativa = cargarVista(() => import('./vistas/Normativa/NuevaNormativa'));
const EditarNormativa = cargarVista(() => import('./vistas/Normativa/EditarNormativa'));
const GestorNormativa = cargarVista(() => import('./vistas/Normativa/GestorNormativa'));
const GestorNormativaUsuario = cargarVista(() => import('./vistas/Normativa/Usuario/GestorNormativaUsuario'));
const TipoDocumento = cargarVista(() => import('./vistas/TipoDocumento/GestorTipoDocumento'));
const ClasificacionDocumento = cargarVista(() => import('./vistas/ClasificacionDocumento/GestorClasificacionDocumento'));
const EstadoTramitacion = cargarVista(() => import('./vistas/EstadoTramitacion/GestorEstadoTramitacion'));
const Clientes = cargarVista(() => import('./vistas/Clientes/GestorClientes'));
const Correos = cargarVista(() => import('./vistas/Correos/GestorCorreos'));
const OrganismoEmisor = cargarVista(() => import('./vistas/OrganismoEmisor/GestorOrganismoEmisor'));
const Rubros = cargarVista(() => import('./vistas/Rubros/GestorRubros'));
const EnlaceCongreso = cargarVista(() => import('./vistas/EnlaceCongreso/GestorEnlaceCongreso'));
const Compromisos = cargarVista(() => import('./vistas/Compromiso/GestorCompromiso'));
const CompromisosUsuario = cargarVista(() => import('./vistas/Compromiso/Usuario/GestorCompromisoUsuario'));
const PerfilUsuario = cargarVista(() => import('./vistas/Usuarios/PerfilUsuario'));
const Usuarios = cargarVista(() => import('./vistas/Usuarios/GestorUsuarios'));
const Logs = cargarVista(() => import('./vistas/Configuracion/Logs/GestorLogs'));
const HorarioCliente = cargarVista(() => import('./vistas/Clientes/HorarioClientes'));
const DetalleCompromiso = cargarVista(() => import('./vistas/Compromiso/DetalleCompromiso'));
const DetalleCompromisoPublico = cargarVista(() => import('./vistas/Compromiso/DetalleCompromisoPublico'));
const GestionOrganismos = cargarVista(() => import('./vistas/Clientes/GestionOrganismos'));
const GestionRubros = cargarVista(() => import('./vistas/Clientes/GestionRubros'));
const ConfigurarSemaforo = cargarVista(() => import('./vistas/Clientes/ConfigurarSemaforo'));
const GestorModulos = cargarVista(() => import('./vistas/Clientes/GestorModulos'));
const GestorObligacionesUsuario = cargarVista(() => import('./vistas/Obligaciones/Usuario/GestorObligacionesUsuario'));
const DetalleObligaciones = cargarVista(() => import('./vistas/Obligaciones/DetalleObligaciones'));
const DetalleObligacionesPublico = cargarVista(() => import('./vistas/Obligaciones/DetalleObligacionesPublico'));
const GestorConfiguracionCorreo = cargarVista(() => import('./vistas/Correos/GestorConfiguracionCorreo'));
const EnvioManualCorreo = cargarVista(() => import('./vistas/Correos/EnvioManualCorreo'));
const IntegracionGRCNormativas = cargarVista(() => import('./vistas/Normativa/Usuario/IntegracionGRCNormativas'));
const GestorPais = cargarVista(() => import('./vistas/Pais/GestorPais'));
const BandejaNormativas = cargarVista(() => import('./vistas/Bandeja/BandejaNormativa'));
const HorarioEnvioScraperPanel = cargarVista(() => import('./vistas/Bandeja/HorarioEnvioScraperPanel'));
const DetalleNormativaPublico = cargarVista(() => import('./vistas/Bandeja/DetalleNormativaPublico'));
const Newsletter = cargarVista(() => import('./vistas/Newsletter/Newsletter'));
const GestorOrgnanizaciones = cargarVista(() => import('./vistas/Clientes/GestorOrganizaciones'));
const GestorNormativaGuardadas = cargarVista(() => import('./vistas/Normativa/Usuario/GestorNormativaGuardadas'));
const AnalizadorNorma = cargarVista(() => import('./vistas/Normativa/AnalizadorNorma'));
const ConfiguracionCupoAnalizador = cargarVista(() => import('./vistas/Configuracion/ConfiguracionCupoAnalizador'));
const DashboardImpacto = cargarVista(() => import('./vistas/Dashboard/DashboardImpacto'));
const BandejaSuscriptor = cargarVista(() => import('./vistas/Suscriptor/BandejaSuscriptor'));
const MiSuscripcion = cargarVista(() => import('./vistas/Suscriptor/MiSuscripcion'));

function MainLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const location = useLocation();

  useUserStatusValidator();

  useEffect(() => {
    if (isSmallScreen) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
    }
  }, [isSmallScreen]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const toggleDrawer = () => {
    setIsOpen(!isOpen);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        handleDrawerToggle={handleDrawerToggle}
        isOpen={isOpen}
        toggleDrawer={toggleDrawer}
      />
      <Sidebar
        mobileOpen={mobileOpen}
        handleDrawerToggle={handleDrawerToggle}
        isOpen={isOpen && !isSmallScreen}
        isSmallScreen={isSmallScreen}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          p: { xs: 1.5, sm: 2.5, md: 3.5 },
          width: {
            xs: '100%',
            sm: `calc(100% - ${(isOpen && !isSmallScreen) ? DRAWER_WIDTH : 0}px)`
          },
          backgroundImage: theme => `radial-gradient(1100px 380px at 100% -120px, ${alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.07 : 0.12)}, transparent 70%)`,
          backgroundRepeat: 'no-repeat',
          transition: theme => theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar sx={{ minHeight: APPBAR_HEIGHT }} />
        <MigasProvider>
          {/* key por ruta: re-dispara la animación de entrada en cada navegación */}
          <Box key={location.pathname} sx={{ animation: 'anFadeUp .35s ease both' }}>
            <MigasAutomaticas />
            <Suspense fallback={<CargandoVista />}>
              <Routes>
                <Route path="/home" element={<Home />} />
                <Route element={<ProtectedRoute allowedRoles={["Administrador", "Usuario"]} />}>
                  <Route path="/usuario/gestor-normativa" element={<GestorNormativaUsuario />} />
                  <Route path="/perfil-usuario" element={<PerfilUsuario />} />
                  <Route path="/usuario/compromisos" element={<CompromisosUsuario />} />
                  <Route path="/usuario/compromisos/normativa/:idNormativa" element={<DetalleCompromiso />} />
                  <Route path="/usuario/obligaciones" element={<GestorObligacionesUsuario />} />
                  <Route path="/usuario/obligaciones/normativa/:idNormativa" element={<DetalleObligaciones />} />
                  <Route path="/usuario/integracion-grc-normativas" element={<IntegracionGRCNormativas />} />
                  <Route path="/usuario/normativas-guardadas" element={<GestorNormativaGuardadas />} />
                  <Route path="/usuario/analizador-norma" element={<AnalizadorNorma />} />
                  <Route path="/dashboard-impacto" element={<DashboardImpacto />} />
                </Route>

                {/* ── Ruta del suscriptor: si no hay sesión, va a /suscriptor/login ── */}
                <Route element={<ProtectedRoute allowedRoles={["Suscriptor", "Administrador"]} />}>
                  <Route path="/suscriptor/bandeja" element={<BandejaSuscriptor />} />
                  <Route path="/suscriptor/mi-suscripcion" element={<MiSuscripcion />} />
                </Route>

                <Route element={<ProtectedRoute allowedRoles={["Administrador"]} />}>
                  <Route path="/perfil-usuario" element={<PerfilUsuario />} />
                  <Route path="/usuarios" element={<Usuarios />} />
                  <Route path="/nueva-normativa" element={<NuevaNormativa />} />
                  <Route path="/gestor-normativa" element={<GestorNormativa />} />
                  <Route path="/editar-normativa/:id" element={<EditarNormativa />} />
                  <Route path="/compromisos" element={<Compromisos />} />
                  <Route path='/tipo-documento' element={<TipoDocumento/>}/>
                  <Route path='/enlace-congreso' element={<EnlaceCongreso/>}/>
                  <Route path='/clasificacion-documento' element={<ClasificacionDocumento/>}/>
                  <Route path='/estado-tramitacion' element={<EstadoTramitacion/>}/>
                  <Route path='/cliente/gestor-clientes' element={<Clientes/>}/>
                  <Route path='/cliente/gestor-organismos' element={<GestionOrganismos/>}/>
                  <Route path='/cliente/gestor-rubros' element={<GestionRubros/>}/>
                  <Route path='/cliente/configurar-semaforo' element={<ConfigurarSemaforo/>}/>
                  <Route path='/cliente/gestor-modulos' element={<GestorModulos/>}/>
                  <Route path='/cliente/correos' element={<Correos/>}/>
                  <Route path='/organismo-emisor' element={<OrganismoEmisor/>}/>
                  <Route path='/rubros' element={<Rubros/>}/>
                  <Route path='/logs' element={<Logs/>}/>
                  <Route path='/cliente/horario-clientes' element={<HorarioCliente/>}/>
                  <Route path="/usuario/compromisos/normativa/:idNormativa" element={<DetalleCompromiso />} />
                  <Route path='/envio-correo-normativa' element={<EnvioManualCorreo/>}/>
                  <Route path='cliente/configuracion-correo' element={<GestorConfiguracionCorreo/>}/>
                  <Route path='/paises' element={<GestorPais/>}/>
                  <Route path='/bandeja-normativas' element={<BandejaNormativas/>}/>
                  <Route path='/newsletter' element={<Newsletter/>}/>
                  <Route path='/horario-envio-scraper' element={<HorarioEnvioScraperPanel/>}/>
                  <Route path='/gestor-organizaciones' element={<GestorOrgnanizaciones/>}/>
                  <Route path='/cliente/cupo-analizador' element={<ConfiguracionCupoAnalizador/>}/>
                </Route>
              </Routes>
            </Suspense>
          </Box>
        </MigasProvider>
      </Box>
    </Box>
  );
}

function App() {
  return (
    <Router>
      <Suspense fallback={<CargandoVista />}>
        <Routes>
          {/* <Route path="/" element={<LandingPage />} /> */}
          <Route path="/login" element={<AuthRedirect><LoginForm /></AuthRedirect>} />

          <Route path="/compromisos/normativa/:idNormativa" element={<DetalleCompromisoPublico />} />
          <Route path="/obligaciones/normativa/:idNormativa" element={<DetalleObligacionesPublico />} />
          <Route path="/bandeja/normativa/:id" element={<DetalleNormativaPublico />} />
          <Route path="/*" element={<AuthCheck><MainLayout /></AuthCheck>} />
        </Routes>
      </Suspense>
    </Router>
  );
}

const AuthCheck = ({ children }) => {
  const token = localStorage.getItem("token");

  if (!token) {
    window.location.href = '/login';
    return null;
  }

  return children;
};

export default App;
